import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CMSPage {
  id: string;
  page_key: string;
  label: string;
  path: string;
  is_enabled: boolean;
  display_order: number;
}

export function useCMSPages() {
  return useQuery({
    queryKey: ["cms-pages"],
    queryFn: async (): Promise<CMSPage[]> => {
      const { data, error } = await supabase
        .from("cms_pages")
        .select("id, page_key, label, path, is_enabled, display_order")
        .order("display_order", { ascending: true });
      if (error) throw error;
      return (data ?? []) as CMSPage[];
    },
    staleTime: 60_000,
  });
}

/** Returns true when the page is enabled (defaults to enabled when unknown). */
export function usePageEnabled(pageKey: string) {
  const { data, isLoading } = useCMSPages();
  const page = data?.find((p) => p.page_key === pageKey);
  return { isEnabled: page ? page.is_enabled : true, isLoading };
}

export function useTogglePage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_enabled }: { id: string; is_enabled: boolean }) => {
      const { error } = await supabase
        .from("cms_pages")
        .update({ is_enabled })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cms-pages"] });
    },
  });
}

/** Reorders pages by writing the new display_order for each page. */
export function useReorderPages() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orderedIds: string[]) => {
      await Promise.all(
        orderedIds.map(async (id, index) => {
          const { error } = await supabase
            .from("cms_pages")
            .update({ display_order: index + 1 })
            .eq("id", id);
          if (error) throw error;
        })
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cms-pages"] });
    },
  });
}
